import qs from 'qs';

import type { actions } from '@luxuryescapes/contract-flights-charter';

import type {
  CharterPromotionPricingInventoryRow,
  ListCharterPromotionPricingInventoryParams,
  ListCharterPromotionPricingInventoryResponse,
  UpsertCharterPromotionPricingInventoryBody,
  UpsertCharterPromotionPricingInventoryResponse,
} from '~/types/charterPromotionPricingInventory';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1/fare-families/promotions`;

type RequestOptions = {
  signal?: AbortSignal;
};

type CharterPromotionPricingInventoryApiResponse =
  actions['promotionFareGetBatch']['responses'][200]['content']['application/json'];
type UpsertCharterPromotionPricingInventoryApiResponse =
  actions['promotionFareUpsert']['responses'][200]['content']['application/json'];
type PromotionPricingInventoryApiRow =
  | CharterPromotionPricingInventoryApiResponse['result'][number]
  | UpsertCharterPromotionPricingInventoryApiResponse['result'];

const contractToUiCabinMap = {
  ECONOMY: 'economy',
  LUX_ROW: 'lux_row',
  PREMIUM_ECONOMY: 'premium_economy',
  BUSINESS: 'business',
} as const;

function toUiCabinClass(cabinClass: string): CharterPromotionPricingInventoryRow['cabin'] {
  return contractToUiCabinMap[cabinClass as keyof typeof contractToUiCabinMap] ?? 'economy';
}

function getPricingPrice(
  pricing: Array<PromotionPricingInventoryApiRow['pricing'][number]>,
  paxType: 'ADT' | 'CHD',
): number {
  return pricing.find((pricingRow) => pricingRow.paxType === paxType)?.price ?? 0;
}

function toCharterPromotionPricingInventoryRow(
  pricingInventory: PromotionPricingInventoryApiRow,
): CharterPromotionPricingInventoryRow {
  const flightInstance = 'flightInstance' in pricingInventory ? pricingInventory.flightInstance : undefined;
  const depAirport = flightInstance?.depAirport ?? '';
  const arrAirport = flightInstance?.arrAirport ?? '';

  return {
    id: pricingInventory.promotionFareId,
    promotionType: pricingInventory.promotionType,
    eligibility: pricingInventory.eligibility,
    flightInstanceId: pricingInventory.flightInstanceId,
    depAirport,
    arrAirport,
    route: depAirport && arrAirport ? `${depAirport} -> ${arrAirport}` : '',
    date: flightInstance?.depDate ?? '',
    cabin: toUiCabinClass(pricingInventory.cabinClass),
    availableDaysBefore: pricingInventory.availableDaysBefore,
    promotionAllocation: pricingInventory.allocation,
    totalBooked: pricingInventory.booked,
    adultPrice: getPricingPrice(pricingInventory.pricing, 'ADT'),
    childPrice: getPricingPrice(pricingInventory.pricing, 'CHD'),
    openForSale: pricingInventory.allocation - pricingInventory.booked,
  };
}

export async function listCharterPromotion(
  promotionType: actions['promotionFareGetBatch']['parameters']['path']['promotionType'],
  params: ListCharterPromotionPricingInventoryParams = {},
  options: RequestOptions = {},
): Promise<ListCharterPromotionPricingInventoryResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  const response: CharterPromotionPricingInventoryApiResponse = await request(
    query ? `${BASE_PATH}/${promotionType}?${query}` : `${BASE_PATH}/${promotionType}`,
    {
      ...options,
      method: 'GET',
    },
  );
  return {
    status: response.status,
    message: response.message,
    result: response.result.map(toCharterPromotionPricingInventoryRow),
    pagination: {
      limit: response.pagination.limit,
      offset: response.pagination.offset,
      totalItems: response.pagination.totalItems,
      hasNext: response.pagination.hasNext,
    },
  };
}

export async function fetchAllCharterPromotions(
  promotionType: actions['promotionFareGetBatch']['parameters']['path']['promotionType'],
): Promise<Array<CharterPromotionPricingInventoryRow>> {
  const pageSize = 1000;
  const rows: Array<CharterPromotionPricingInventoryRow> = [];
  let offset = 0;
  let hasNext = true;

  while (hasNext) {
    const response = await listCharterPromotion(promotionType, { offset, limit: pageSize });
    rows.push(...response.result);
    hasNext = response.pagination.hasNext && response.result.length > 0;
    offset += response.pagination.limit;
  }

  return rows;
}

export async function upsertCharterPromotion(
  promotionType: actions['promotionFareUpsert']['parameters']['path']['promotionType'],
  body: UpsertCharterPromotionPricingInventoryBody,
): Promise<UpsertCharterPromotionPricingInventoryResponse> {
  const response: UpsertCharterPromotionPricingInventoryApiResponse = await request(`${BASE_PATH}/${promotionType}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(body),
  });
  return {
    status: response.status,
    message: response.message,
    result: toCharterPromotionPricingInventoryRow(response.result),
  };
}
